import { ApolloError } from "@apollo/client";
import { Alert, Divider, Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import {
    GqlSettingsFormTemplateQuestionCondition,
    GqlSettingsFormTemplateQuestionTag,
    GqlSettingsFormTemplateQuestionType,
} from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import { Shared_FormTemplate_QuestionFragment } from "~/shared/components/FormTemplate/fragments/fragments.generated";
import { splitGqlUnion } from "~/shared/helpers/splitGqlUnion";

import isFormTemplateQuestionDropdownOrSelect from "../../util/isFormTemplateQuestionDropdownOrSelect";
import QuestionConditionFields from "./QuestionConditionFields";
import QuestionCoreFields from "./QuestionCoreFields";
import QuestionFieldSettings from "./QuestionFieldSettings";
import SelectableQuestionOptionsEditor from "./SelectableQuestionOptionsEditor";

type Props = {
    initialData?: FormTemplateQuestionFormInputs;
    error?: ApolloError;
    onSubmit: (value: FormTemplateQuestionFormInputs) => void;
    actions: (isDirtyState: boolean, canSave: boolean) => ReactNode;
    selectableConditionQuestions: Shared_FormTemplate_QuestionFragment[];
};

export default function FormTemplateQuestionForm({
    initialData,
    error,
    onSubmit,
    actions,
    selectableConditionQuestions,
}: Props) {
    const { t } = useTranslation("formTemplateEditor");

    const formMethods = useForm<FormTemplateQuestionFormInputs>({
        values: initialData,
        defaultValues: {
            isRequired: false,
            showToClientUser: false,
            condition: {
                __typename: "GqlSettingsFormTemplateQuestionConditionNone",
            } as GqlSettingsFormTemplateQuestionCondition,
        },
    });

    const {
        handleSubmit,
        setError,
        watch,
        formState: { errors, isDirty, isSubmitted, isSubmitting },
    } = formMethods;

    const selectedType = watch("type");
    const options = watch("options");

    const hasOptions = (options?.length ?? 0) > 0;

    const requiresOptions = isFormTemplateQuestionDropdownOrSelect(selectedType?.__typename);

    const canSave = !requiresOptions || hasOptions;

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);
    useApplyErrorToForm(error, setError);

    const questionTypeSpecificFields =
        selectedType?.__typename &&
        splitGqlUnion(selectedType, {
            GqlSettingsFormTemplateQuestionTypeText: () => (
                <SwitchElement name="multiline" label={t("formTemplateQuestionForm.labelMultiline")} />
            ),
            GqlSettingsFormTemplateQuestionTypeDropdown: () => (
                <SelectableQuestionOptionsEditor optionVariant="dropdown" />
            ),
            GqlSettingsFormTemplateQuestionTypeSelect: () => <SelectableQuestionOptionsEditor optionVariant="radio" />,
            GqlSettingsFormTemplateQuestionTypeAmount: () => null,
            GqlSettingsFormTemplateQuestionTypeRecording: () => null,
            GqlSettingsFormTemplateQuestionTypeDateTime: () => null,
        });

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <GaiaConfirmCloseDialog blocker={blocker} />
                <Stack gap={2}>
                    {errors.root && (
                        <Alert severity="error">
                            <Typography>{errors.root.message}</Typography>
                        </Alert>
                    )}
                    <QuestionCoreFields />
                    {questionTypeSpecificFields && (
                        <>
                            <Divider textAlign="center">{t("formTemplateQuestionForm.fieldOptionsDivider")}</Divider>
                            {questionTypeSpecificFields}
                        </>
                    )}
                    <Divider textAlign="center">{t("formTemplateQuestionForm.fieldSettingsDivider")}</Divider>
                    <QuestionFieldSettings />
                    <Divider textAlign="center">{t("formTemplateQuestionForm.fieldConditionDivider")}</Divider>
                    <QuestionConditionFields selectableConditionQuestions={selectableConditionQuestions} />
                    {actions(!isDirty, canSave)}
                </Stack>
            </form>
        </FormProvider>
    );
}

export type FormTemplateQuestionFormInputs = {
    name: string;
    isRequired: boolean;
    showToClientUser: boolean;
    description: string;
    placeholder: string;
    tags?: GqlSettingsFormTemplateQuestionTag[] | null;
    type: GqlSettingsFormTemplateQuestionType;
    multiline?: boolean;
    options?: { id: string; name: string; key: string }[];
    condition: GqlSettingsFormTemplateQuestionCondition;
    conditionSelectedQuestion?: string;
    conditionSelectedQuestionOption?: string;
    conditionOrConditions?: {
        id: string;
        conditionSelectedQuestion?: string;
        conditionSelectedQuestionOption?: string;
    }[];
};
